import { useMemo } from 'react';

import { Pressable, Typography } from '@rbilabs/universal-components';
import { View } from 'react-native';

import Picture from 'components/picture';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { parseStringifiedJSON } from 'utils/parse-string';

import { CheckboxIcon } from './checkbox-icon';
import { PriceAndCalories } from './price-and-calories';
import { ProductOptions } from './product-options';
import {
  CheckMarkContainer,
  ProductCardContainer,
  ProductCardContent,
  ProductCardFooter,
  ProductCardImageContainer,
  ProductNameContainer,
} from './styled';
import { IProductCarModifierdProps } from './types';

const PRODUCT_CARD_WIDTH = '33.3%';
const PRODUCT_CARD_PADDING = 1;
const DEFAULT_PRODUCT_CARD_HEIGHT_MOBILE = 180;
const DEFAULT_PRODUCT_CARD_HEIGHT_DESKTOP = 200;

export const ProductCardModifierExperiment = ({
  isSelected,
  onCardPress,
  accessibilityRole,
  accessibilitySelected,
  productProps,
  imgObjectFitContain,
  options,
  onOptionPress,
  selectedOption,
  multipleChoiceQuantity = false,
}: IProductCarModifierdProps) => {
  const isMobile = useIsMobileBreakpoint();

  const { name, image, fallbackImage, loading, price, calories } = productProps;

  const selection = useMemo(
    () => (typeof selectedOption === 'string' ? selectedOption : selectedOption?._key ?? ''),
    [selectedOption]
  );

  if (!name && !loading) {
    return null;
  }
  const defaultProductCardHeight = isMobile
    ? DEFAULT_PRODUCT_CARD_HEIGHT_MOBILE
    : DEFAULT_PRODUCT_CARD_HEIGHT_DESKTOP;

  const productName = name?.trim();

  return (
    <Pressable
      onPress={onCardPress}
      width={PRODUCT_CARD_WIDTH}
      padding={PRODUCT_CARD_PADDING}
      key="product-card-animation"
      accessibilityRole={accessibilityRole}
      {...(accessibilityRole === 'radio' && {
        accessibilityChecked: accessibilitySelected,
      })}
    >
      <ProductCardContainer
        $isSelected={isSelected}
        height={defaultProductCardHeight}
        testID="product-card-container"
      >
        <CheckMarkContainer $isSelected={isSelected}>
          <CheckboxIcon size={6} $isSelected={isSelected} />
        </CheckMarkContainer>

        <ProductCardContent>
          {(image || fallbackImage) && (
            <ProductCardImageContainer key={productName} testID="product-image">
              {image && (
                <Picture
                  width="full"
                  flex={1}
                  image={parseStringifiedJSON({ value: image })}
                  alt={`${t('product')} ${productName}`}
                  accessibilityHidden
                  objectFitContain={imgObjectFitContain}
                />
              )}
              {!image &&
                fallbackImage &&
                fallbackImage({
                  width: 55,
                  height: 55,
                })}
            </ProductCardImageContainer>
          )}
        </ProductCardContent>

        <ProductCardFooter testID="product-card-footer">
          <ProductNameContainer>
            <Typography
              fontSize="sm"
              textAlign="center"
              numberOfLines={2}
              bold
              maxFontSizeMultiplier={2}
            >
              {productName}
            </Typography>
          </ProductNameContainer>

          <View testID="product-card-price-and-calories">
            <PriceAndCalories
              price={price}
              calories={calories}
              showPriceUpcharge={productProps.showPriceUpcharge}
            />
          </View>

          {!!options?.length && (
            <ProductOptions
              isSelected={isSelected}
              multipleChoiceQuantity={multipleChoiceQuantity}
              selection={selection}
              options={options}
              onOptionPress={onOptionPress}
            />
          )}
        </ProductCardFooter>
      </ProductCardContainer>
    </Pressable>
  );
};
